<template>
  <div class="protected-avatar isolate" :style="{ width: size + 'px', height: size + 'px' }">
    <!-- isolate: o z-index do escudo vale só dentro do avatar; sem isso ele furava overlays
         da página (o conteúdo do DashboardLayout tem stacking context próprio, z-index 0). -->
    <canvas v-show="ready" ref="canvasRef" class="protected-avatar__canvas" />
    <div v-if="!ready" class="protected-avatar__fallback">
      <slot />
    </div>
    <div class="protected-avatar__shield" @contextmenu.prevent @dragstart.prevent />
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onBeforeUnmount, nextTick } from 'vue'
import { drawCircleCover, fetchAsImageBitmap, loadViaImage } from '@/utils/protectedImage'

const props = withDefaults(
  defineProps<{
    src?: string | null
    size?: number
  }>(),
  { size: 40 }
)

const canvasRef = ref<HTMLCanvasElement | null>(null)
const ready = ref(false)

let currentBitmap: ImageBitmap | null = null
let loadGeneration = 0
let abortController: AbortController | null = null

function draw() {
  const canvas = canvasRef.value
  if (!currentBitmap || !canvas) return
  drawCircleCover(canvas, currentBitmap, props.size, window.devicePixelRatio || 1)
}

function dispose() {
  ++loadGeneration
  if (abortController) {
    abortController.abort()
    abortController = null
  }
  if (currentBitmap) {
    currentBitmap.close()
    currentBitmap = null
  }
  ready.value = false
}

async function load(url: string) {
  dispose()
  const generation = ++loadGeneration
  abortController = new AbortController()

  try {
    let bitmap: ImageBitmap
    try {
      bitmap = await fetchAsImageBitmap(url, abortController.signal)
    } catch {
      if (generation !== loadGeneration) return
      // Fallback: legacy Azure Blob URLs may not have CORS configured
      bitmap = await loadViaImage(url)
    }
    if (generation !== loadGeneration) {
      bitmap.close()
      return
    }
    currentBitmap = bitmap
    ready.value = true
    await nextTick()
    draw()
  } catch {
    if (generation === loadGeneration) ready.value = false
  }
}

watch(
  () => props.src,
  (url) => {
    if (url) load(url)
    else dispose()
  },
  { immediate: true }
)

watch(
  () => props.size,
  () => {
    if (currentBitmap) {
      nextTick().then(draw)
    }
  }
)

onBeforeUnmount(() => {
  ++loadGeneration
  dispose()
})
</script>

<style scoped>
.protected-avatar {
  position: relative;
  flex-shrink: 0;
}

.protected-avatar__canvas {
  width: 100%;
  height: 100%;
  display: block;
}

.protected-avatar__fallback {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  overflow: hidden;
}

.protected-avatar__shield {
  position: absolute;
  inset: 0;
  z-index: 1;
  background: transparent;
}
</style>
